/**
 * A code-drawn placeholder captain portrait (M6 plan P32, §9): the seeded silhouette, initials and faction tint of
 * a `PortraitRecipe` (`models/portrait.ts`), as an inline SVG. Decorative: the captain's name is always shown next
 * to it, so it is hidden from assistive tech.
 */
import type { ReactNode } from 'react'
import type { PortraitRecipe } from './models/portrait'

export interface PortraitProps {
  recipe: PortraitRecipe
  /** Rendered size in CSS pixels (square). */
  size?: number
  className?: string
}

export function Portrait(p: PortraitProps): ReactNode {
  const r = p.recipe
  const size = p.size ?? 48
  return (
    <svg
      className={`cp-portrait${p.className === undefined ? '' : ` ${p.className}`}`}
      width={size}
      height={size}
      viewBox="0 0 64 64"
      aria-hidden="true"
      focusable="false"
      data-testid="captain-portrait"
      data-hair={r.hair}
      data-collar={r.collar}
    >
      <rect x="0" y="0" width="64" height="64" fill={r.bg} />
      <path d={r.shoulders} fill={r.fg} fillOpacity={0.55} />
      <ellipse cx={r.head.cx} cy={r.head.cy} rx={r.head.rx} ry={r.head.ry} fill={r.fg} fillOpacity={0.7} />
      {r.hairPath === '' ? null : <path d={r.hairPath} fill={r.fg} />}
      {r.collarPath === '' ? null : <path d={r.collarPath} fill="none" stroke={r.bg} strokeWidth={1.5} strokeOpacity={0.8} />}
      <rect x="40" y="48" width="22" height="14" rx="2" fill={r.bg} fillOpacity={0.85} />
      <text x="51" y="58.5" textAnchor="middle" fontSize="9" fontWeight="700" fill={r.fg} fontFamily="inherit">
        {r.initials}
      </text>
    </svg>
  )
}
